import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";

type SessionRow = {
  sessionName: string;
  startTime: string;
  endTime: string;
};

type GeneralOpeningHoursTabProps = {
  /** Clinic day start from Manage time → Clinic Hours (`formData.from`), e.g. `09:00` */
  clinicSessionStart?: string;
  /** Clinic day end (`formData.to`), e.g. `17:00` */
  clinicSessionEnd?: string;
};

function normalizeTimeForInput(value: string | undefined, fallback: string): string {
  const v = (value ?? "").trim();
  if (!v) return fallback;
  const match = v.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return fallback;
  const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
  const min = Math.min(59, Math.max(0, parseInt(match[2], 10)));
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/** Nearest :00 or :30 for 30-minute slot pickers. */
function snapTo30Min(value: string | undefined, fallback: string): string {
  const base = normalizeTimeForInput(value, fallback);
  const match = base.match(/^(\d{2}):(\d{2})/);
  if (!match) return fallback;
  let h = parseInt(match[1], 10);
  let m = parseInt(match[2], 10);
  if (m < 15) m = 0;
  else if (m < 45) m = 30;
  else {
    m = 0;
    h = Math.min(23, h + 1);
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function buildHalfHourTimeOptions(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, "0");
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
}

function timeToMinutes(t: string): number {
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/** Half-hour slots from clinic open through clinic close (inclusive). */
function filterOptionsWithinClinicWindow(
  allHalfHours: string[],
  clinicOpen: string,
  clinicClose: string
): string[] {
  const lo = timeToMinutes(clinicOpen);
  const hi = timeToMinutes(clinicClose);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return allHalfHours;
  const a = Math.min(lo, hi);
  const b = Math.max(lo, hi);
  const filtered = allHalfHours.filter((t) => {
    const m = timeToMinutes(t);
    return m >= a && m <= b;
  });
  return filtered.length > 0 ? filtered : allHalfHours;
}

function pickStartInOptions(want: string, options: string[]): string {
  if (options.length === 0) return want;
  if (options.includes(want)) return want;
  const w = timeToMinutes(want);
  const sorted = [...options].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
  const atOrAfter = sorted.find((t) => timeToMinutes(t) >= w);
  if (atOrAfter) return atOrAfter;
  return sorted[sorted.length - 1];
}

function pickEndInOptions(want: string, options: string[]): string {
  if (options.length === 0) return want;
  if (options.includes(want)) return want;
  const w = timeToMinutes(want);
  const sorted = [...options].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
  const atOrBefore = [...sorted].reverse().find((t) => timeToMinutes(t) <= w);
  if (atOrBefore) return atOrBefore;
  return sorted[0];
}

/** Compact time picker: scroll-limited list (native select cannot cap menu height). */
function ScrollableTimeSelect({
  id,
  ariaLabelledBy,
  value,
  options,
  onChange,
}: {
  id: string;
  ariaLabelledBy: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={ariaLabelledBy}
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 rounded-md border border-slate-200 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-left text-sm text-slate-800 flex items-center justify-between gap-2"
      >
        <span>{value}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <ul
          role="listbox"
          aria-labelledby={ariaLabelledBy}
          className="absolute left-0 right-0 top-full z-[100] mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {options.map((t) => (
            <li
              key={t}
              role="option"
              aria-selected={t === value}
              className={[
                "px-3 py-2 text-sm cursor-pointer select-none",
                t === value
                  ? "bg-slate-100 font-medium text-slate-900"
                  : "text-slate-700 hover:bg-slate-50",
              ].join(" ")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
            >
              {t}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function GeneralOpeningHoursTab({
  clinicSessionStart,
  clinicSessionEnd,
}: GeneralOpeningHoursTabProps) {
  const weekTabs = useMemo(
    () => [
      { week: "Monday", abbreviation: "Mon" },
      { week: "Tuesday", abbreviation: "Tue" },
      { week: "Wednesday", abbreviation: "Wed" },
      { week: "Thursday", abbreviation: "Thu" },
      { week: "Friday", abbreviation: "Fri" },
      { week: "Saturday", abbreviation: "Sat" },
      { week: "Sunday", abbreviation: "Sun" },
    ],
    []
  );

  const [selectedDay, setSelectedDay] = useState(weekTabs[0].week);
  const [rowsByDay, setRowsByDay] = useState<Record<string, SessionRow[]>>({});
  const [loading, setLoading] = useState(false);

  const halfHourOptions = useMemo(() => buildHalfHourTimeOptions(), []);

  const defaultStartTime = useMemo(
    () => snapTo30Min(clinicSessionStart, "09:00"),
    [clinicSessionStart]
  );
  const defaultEndTime = useMemo(
    () => snapTo30Min(clinicSessionEnd, "17:00"),
    [clinicSessionEnd]
  );

  /** Allowed times: within clinic session bounds only. */
  const clinicHalfHourOptions = useMemo(
    () =>
      filterOptionsWithinClinicWindow(
        halfHourOptions,
        defaultStartTime,
        defaultEndTime
      ),
    [halfHourOptions, defaultStartTime, defaultEndTime]
  );

  const rows = rowsByDay[selectedDay] ?? [];

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newSession, setNewSession] = useState<SessionRow>({
    sessionName: "",
    startTime: "09:00",
    endTime: "17:00",
  });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editSession, setEditSession] = useState<SessionRow | null>(null);
  const [editSessionName, setEditSessionName] = useState("");

  const refreshSessions = async (day: string) => {
    setLoading(true);
    try {
      const response = await baseUrl.get(
        `/client/setting/generalOpenHours/findGeneralOpenHours?day=${day}`
      );
      const apiSessions = response.data?.data?.sessions ?? [];
console.log(apiSessions);
      const sessionRows: SessionRow[] = Array.isArray(apiSessions)
        ? apiSessions.map((s: any) => ({
            sessionName: String(s?.sessionName ?? ""),
            startTime: String(s?.startTime ?? ""),
            endTime: String(s?.endTime ?? ""),
          }))
        : [];

      sessionRows.sort(
        (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
      );

      setRowsByDay((prev) => ({ ...prev, [day]: sessionRows }));
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load sessions"
      );
      setRowsByDay((prev) => ({ ...prev, [day]: [] }));
    } finally {
      setLoading(false);
    }
  };

  const resetNewSessionDefaults = () => ({
    sessionName: "",
    startTime: defaultStartTime,
    endTime: defaultEndTime,
  });

  const openCreate = () => {
    setNewSession(resetNewSessionDefaults());
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setNewSession(resetNewSessionDefaults());
  };

  const openEdit = (row: SessionRow) => {
    setEditSession(row);
    setEditSessionName(row.sessionName);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditSession(null);
    setEditSessionName("");
  };

  const startSnapped = snapTo30Min(newSession.startTime, defaultStartTime);
  const endSnapped = snapTo30Min(newSession.endTime, defaultEndTime);

  let startSelectOptions = clinicHalfHourOptions.filter(
    (t) => timeToMinutes(t) <= timeToMinutes(endSnapped)
  );
  if (startSelectOptions.length === 0 && clinicHalfHourOptions.includes(endSnapped)) {
    startSelectOptions = [endSnapped];
  }

  let endSelectOptions = clinicHalfHourOptions.filter(
    (t) => timeToMinutes(t) >= timeToMinutes(startSnapped)
  );
  if (endSelectOptions.length === 0 && clinicHalfHourOptions.includes(startSnapped)) {
    endSelectOptions = [startSnapped];
  }

  const startSelectValue = pickStartInOptions(startSnapped, startSelectOptions);
  const endSelectValue = pickEndInOptions(endSnapped, endSelectOptions);

  const editStartSnapped = editSession
    ? snapTo30Min(editSession.startTime, defaultStartTime)
    : defaultStartTime;
  const editEndSnapped = editSession
    ? snapTo30Min(editSession.endTime, defaultEndTime)
    : defaultEndTime;

  let editStartSelectOptions = clinicHalfHourOptions.filter(
    (t) => timeToMinutes(t) <= timeToMinutes(editEndSnapped)
  );
  if (
    editStartSelectOptions.length === 0 &&
    clinicHalfHourOptions.includes(editEndSnapped)
  ) {
    editStartSelectOptions = [editEndSnapped];
  }

  let editEndSelectOptions = clinicHalfHourOptions.filter(
    (t) => timeToMinutes(t) >= timeToMinutes(editStartSnapped)
  );
  if (
    editEndSelectOptions.length === 0 &&
    clinicHalfHourOptions.includes(editStartSnapped)
  ) {
    editEndSelectOptions = [editStartSnapped];
  }

  const editStartSelectValue = pickStartInOptions(
    editStartSnapped,
    editStartSelectOptions
  );
  const editEndSelectValue = pickEndInOptions(
    editEndSnapped,
    editEndSelectOptions
  );

  const onSaveSession = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: SessionRow = {
      sessionName: newSession.sessionName.trim(),
      startTime: startSelectValue,
      endTime: endSelectValue,
    };
    if (!trimmed.sessionName) return;
    if (!trimmed.startTime || !trimmed.endTime) {
      toast.error("Start and end time are required");
      return;
    }

    baseUrl
      .post("/client/setting/generalOpenHours/createSession", {
        day: selectedDay,
        sessionName: trimmed.sessionName,
        startTime: trimmed.startTime,
        endTime: trimmed.endTime,
        active: true,
      })
      .then((res) => {
        toast.success(res.data?.message || "Session created");
        closeCreate();
        refreshSessions(selectedDay);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to save session"
        );
      });
  };

  const onSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSession) return;

    const nextName = editSessionName.trim();
    if (!nextName) return;

    const startMinutes = timeToMinutes(editStartSelectValue);
    const endMinutes = timeToMinutes(editEndSelectValue);
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return;
    if (endMinutes <= startMinutes) {
      toast.error("End time must be greater than start time");
      return;
    }

    baseUrl
      .post("/client/setting/generalOpenHours/createSession", {
        day: selectedDay,
        sessionName: nextName,
        oldSessionName: editSession.sessionName,
        startTime: editStartSelectValue,
        endTime: editEndSelectValue,
        active: true,
      })
      .then((res) => {
        toast.success(res.data?.message || "Session updated");
        closeEdit();
        refreshSessions(selectedDay);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to update session"
        );
      });
  };

  const onDeleteSession = (row: SessionRow) => {
    const ok = window.confirm(
      `Delete session "${row.sessionName}" for ${selectedDay}?`
    );
    if (!ok) return;

    baseUrl
      .post("/client/setting/generalOpenHours/deleteSession", {
        day: selectedDay,
        sessionName: row.sessionName,
      })
      .then((res) => {
        toast.success(res.data?.message || "Session deleted");
        refreshSessions(selectedDay);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to delete session"
        );
      });
  };

  useEffect(() => {
    refreshSessions(selectedDay);
  }, [selectedDay]);
  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sm:mx-auto sm:max-w-6xl">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-slate-800">
            General Opening hours
          </h3>
          <div className="flex flex-wrap gap-2">
            {weekTabs.map((d) => (
              <button
                key={d.week}
                type="button"
                onClick={() => setSelectedDay(d.week)}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white active:scale-[0.98]",
                  selectedDay === d.week
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300",
                ].join(" ")}
              >
                {window.innerWidth > 500 ? d.week : d.abbreviation}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-3">
          <div className="text-sm text-slate-600">{selectedDay}</div>
          <button
            type="button"
            onClick={openCreate}
            className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
          >
            Create Session
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-center">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[180px]">
                Session Name
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                Start Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                End Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-sm text-slate-500 text-center"
                >
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-sm text-slate-500 text-center"
                >
                  No sessions for this day. Create one to get started.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr
                  key={`${r.sessionName}-${idx}`}
                  className="hover:bg-slate-50/50"
                >
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.sessionName}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600">{r.startTime}</td>
                  <td className="px-2 py-2 text-sm text-slate-600">{r.endTime}</td>
                  <td className="px-2 py-2 text-sm">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="px-2 py-1 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteSession(r)}
                        className="px-2 py-1 rounded-md text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isCreateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeCreate}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md">
            <form
              onSubmit={onSaveSession}
              className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-visible"
            >
              <div className="pl-3 py-1 border-b border-slate-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  Create Session
                </div>
                <button
                  type="button"
                  onClick={closeCreate}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="text-sm text-slate-600">
                  Day: <span className="font-semibold text-slate-800">{selectedDay}</span>
                </div>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Session Name
                  </div>
                  <input
                    value={newSession.sessionName}
                    onChange={(e) =>
                      setNewSession((p) => ({ ...p, sessionName: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-md border border-slate-200 outline-none focus:border-primary"
                    placeholder="e.g. Morning Session"
                    required
                  />
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div
                      className="text-xs font-semibold text-slate-600"
                      id="general-oh-start-lbl"
                    >
                      Start Time
                    </div>
                    <ScrollableTimeSelect
                      id="general-oh-start"
                      ariaLabelledBy="general-oh-start-lbl"
                      value={startSelectValue}
                      options={startSelectOptions}
                      onChange={(start) => {
                        setNewSession((p) => {
                          const end = snapTo30Min(p.endTime, defaultEndTime);
                          const nextEnd =
                            timeToMinutes(end) < timeToMinutes(start) ? start : end;
                          return { ...p, startTime: start, endTime: nextEnd };
                        });
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <div
                      className="text-xs font-semibold text-slate-600"
                      id="general-oh-end-lbl"
                    >
                      End Time
                    </div>
                    <ScrollableTimeSelect
                      id="general-oh-end"
                      ariaLabelledBy="general-oh-end-lbl"
                      value={endSelectValue}
                      options={endSelectOptions}
                      onChange={(end) => {
                        setNewSession((p) => {
                          const start = snapTo30Min(p.startTime, defaultStartTime);
                          const nextStart =
                            timeToMinutes(start) > timeToMinutes(end) ? end : start;
                          return { ...p, startTime: nextStart, endTime: end };
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditOpen && editSession && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeEdit}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md">
            <form
              onSubmit={onSaveEdit}
              className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-visible"
            >
              <div className="pl-3 py-1 border-b border-slate-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  Edit Session
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="text-sm text-slate-600">
                  Day:{" "}
                  <span className="font-semibold text-slate-800">
                    {selectedDay}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div
                      className="text-xs font-semibold text-slate-600"
                      id="general-oh-edit-start-lbl"
                    >
                      Start Time
                    </div>
                    <ScrollableTimeSelect
                      id="general-oh-edit-start"
                      ariaLabelledBy="general-oh-edit-start-lbl"
                      value={editStartSelectValue}
                      options={editStartSelectOptions}
                      onChange={(start) => {
                        setEditSession((p) => {
                          if (!p) return p;
                          const end = snapTo30Min(p.endTime, defaultEndTime);
                          const nextEnd =
                            timeToMinutes(end) < timeToMinutes(start)
                              ? start
                              : end;
                          return { ...p, startTime: start, endTime: nextEnd };
                        });
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <div
                      className="text-xs font-semibold text-slate-600"
                      id="general-oh-edit-end-lbl"
                    >
                      End Time
                    </div>
                    <ScrollableTimeSelect
                      id="general-oh-edit-end"
                      ariaLabelledBy="general-oh-edit-end-lbl"
                      value={editEndSelectValue}
                      options={editEndSelectOptions}
                      onChange={(end) => {
                        setEditSession((p) => {
                          if (!p) return p;
                          const start = snapTo30Min(
                            p.startTime,
                            defaultStartTime
                          );
                          const nextStart =
                            timeToMinutes(start) > timeToMinutes(end)
                              ? end
                              : start;
                          return { ...p, startTime: nextStart, endTime: end };
                        });
                      }}
                    />
                  </div>
                </div>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Session Name
                  </div>
                  <input
                    value={editSessionName}
                    onChange={(e) => setEditSessionName(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 outline-none focus:border-primary"
                    placeholder="e.g. Session 1"
                    required
                  />
                </label>
              </div>

              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
